import React from 'react';
import { Skeleton } from '@mui/material';
import { grey, lightBlue } from '@mui/material/colors';
import { BarChart } from '@mui/x-charts';
import { LenderConversionTableRowViewModel, LenderPerformanceReportFilters } from '@trnsact/trnsact-shared-types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { LOOK_TO_BOOK_CHART_HEIGHT } from '../../../../constants';
import { formatPercentValue, mapLookToBookByLenderDataset } from '../../../../lib';
import moment from 'moment';
import { AsyncResource } from '../../../../types';

interface Props {
  resource: AsyncResource<LenderConversionTableRowViewModel[] | null>;
  filters?: LenderPerformanceReportFilters | null;
}

const LABEL_CHAR_WIDTH = 6;

export const LookToBookByLenderChart = ({ resource, filters }: Props) => {
  const dataset = mapLookToBookByLenderDataset(resource.data ?? []);
  const created = filters?.created;
  const period = created
    ? `${moment(created.from).format('MMM D, YYYY')} – ${moment(created.to).format('MMM D, YYYY')}`
    : '';
  const subTitle = `With approval rate overlay — ${period}`;

  const maxLenderNameLength = dataset.reduce((max, row) => Math.max(max, row.lender.length), 0);
  const leftMargin = maxLenderNameLength * LABEL_CHAR_WIDTH;

  return (
    <CardContainer title="Look-to-Book by Lender" subTitle={subTitle}>
      {resource.loading ? (
        <Skeleton variant="rounded" height={LOOK_TO_BOOK_CHART_HEIGHT} />
      ) : (
        <BarChart
          height={LOOK_TO_BOOK_CHART_HEIGHT}
          layout="horizontal"
          dataset={dataset}
          margin={{ left: leftMargin, top: 70 }}
          barLabel={item => {
            const row = dataset[item.dataIndex];
            if (!row) return '';
            const value = item.seriesId === 'approvalGap' ? row.approvalRate : row.lookToBook;
            return value ? formatPercentValue(value) : '';
          }}
          yAxis={[{ scaleType: 'band', dataKey: 'lender' }]}
          xAxis={[{ min: 0, max: 100, tickMinStep: 20, valueFormatter: value => formatPercentValue(value as number) }]}
          series={[
            {
              id: 'lookToBook',
              stack: 'total',
              dataKey: 'lookToBook',
              label: 'Look-to-Book',
              color: lightBlue[200],
              valueFormatter: formatPercentValue,
            },
            {
              id: 'approvalGap',
              stack: 'total',
              dataKey: 'approvalGap',
              label: 'Approval Rate',
              color: grey[300],
              valueFormatter: (value, context) => {
                const row = dataset[context.dataIndex];
                return row ? formatPercentValue(row.approvalRate) : formatPercentValue(value);
              },
            },
          ]}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'left' },
              itemGap: 24,
              markGap: 8,
              itemMarkWidth: 16,
              itemMarkHeight: 16,
              padding: { bottom: 20 },
            },
          }}
        />
      )}
    </CardContainer>
  );
};
